"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { ChevronsDownUp, ChevronsUpDown, Download } from "lucide-react";
import { useMemo, useState } from "react";

import { toCsv } from "../../_logic/export/csv";
import { formatTime } from "@/features/flight-estimator/_logic/utils";
import FetchModal from "@/fetch-ui/FetchModal";

import { useEstimator } from "../../_context/EstimatorContext";
import { buildCityFlights } from "../../_logic/city-flights";
import { PopoverSelect } from "../ui/PopoverSelect";
import { formatTripDates } from "../../_logic/trip-dates";
import { DepartureRow } from "./DepartureRow";

type Sort = "total" | "name";

const SORT_OPTIONS = [
  { value: "total", label: "Total cost" },
  { value: "name", label: "A to Z" },
];

interface CityFlightsPanelProps {
  /** Destination city to show, or null when closed. */
  city: string | null;
  onClose: () => void;
}

/**
 * Detailed flight costs for one destination, ported from the flight estimator: totals across
 * everyone, then each departure airport with its traveler groups (class, flight, time,
 * layovers, cost). Counts, classes and fares are editable in place through the workspace.
 * @param props - The city to show and the close handler.
 * @returns The panel.
 * @throws Never.
 */
export const CityFlightsPanel = ({ city, onClose }: CityFlightsPanelProps) => {
  // Hooks
  const { workspace, flights, money } = useEstimator();

  // States
  const [sort, setSort] = useState<Sort>("total");
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  // Derived
  const details = useMemo(
    () => (city ? buildCityFlights(workspace, flights.entry, city) : null),
    [city, workspace, flights.entry]
  );
  const departures = useMemo(
    () =>
      [...(details?.departures ?? [])].sort((a, b) =>
        sort === "total" ? b.total - a.total : a.code.localeCompare(b.code)
      ),
    [details, sort]
  );
  const allExpanded = departures.length > 0 && departures.every((departure) => expanded.has(departure.code));
  const searching = flights.status === "searching";

  // Handlers
  const toggle = (code: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const exportCsv = () => {
    if (!details || !city) return;
    const rows = [
      ["From", "Group", "Class", "Travelers", "Flight", "Flight time", "Layovers", "Cost per traveler", "Total"],
      ...departures.flatMap((departure) =>
        departure.groups.map((group) => [
          departure.code,
          group.origin.group ?? group.origin.city,
          group.cabin,
          group.origin.count,
          group.result?.flight?.flightNumber ?? "",
          group.result?.flight?.minutes ? formatTime(group.result.flight.minutes) : "",
          group.result?.flight?.layovers ?? "",
          group.costPerTraveler === null
            ? ""
            : Math.round(money.fromUsd(group.costPerTraveler) ?? group.costPerTraveler),
          group.total === null ? "" : Math.round(money.fromUsd(group.total) ?? group.total),
        ])
      ),
    ];
    const csv = toCsv(rows);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `Flights to ${city}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Render
  return (
    <FetchModal
      isOpen={city !== null}
      onClose={onClose}
      size="xl"
      desktopPlacement="top"
      mobileSheet
      title={city ? `Flights to ${city}` : undefined}
      subtitle={
        workspace.dates
          ? `${formatTripDates(workspace.dates)} · round trip · ${details?.travelers ?? 0} travelers`
          : "Add dates to price flights"
      }
    >
      {details && (
        <div className="flex flex-col gap-5">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-gray-100 pb-5 text-sm font-medium sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Total flights", details.pricedTravelers ? money.format(details.total) : "–", "text-lg font-bold"],
              ["Travelers", String(details.travelers), ""],
              ["Avg per traveler", details.avgCost === null ? "–" : money.format(details.avgCost), ""],
              ["Avg flight time", details.avgMinutes === null ? "–" : formatTime(details.avgMinutes), ""],
              ["Layovers", String(details.layovers), ""],
              ["Carbon", details.carbonKg ? `${details.carbonKg.toLocaleString()} kg` : "–", ""],
            ].map(([label, value, emphasis]) => (
              <div key={label} className="flex flex-col gap-0.5">
                <dt className="text-xs font-medium text-gray-600">{label}</dt>
                <dd className={`tabular-nums text-gray-900 ${emphasis}`}>{value}</dd>
              </div>
            ))}
          </dl>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-500">
              {searching
                ? `Searching flights${flights.progress ? ` · ${Math.round(flights.progress * 100)}%` : ""}`
                : details.pricedTravelers < details.travelers
                  ? `${details.travelers - details.pricedTravelers} travelers have no fare yet`
                  : "Every traveler has a fare"}
            </span>
            <div className="flex items-center gap-2">
              <PopoverSelect
                ariaLabel="Sort by"
                options={SORT_OPTIONS}
                value={sort}
                onChange={(value) => setSort(value as Sort)}
              />
              <FetchButton
                variant="ghost"
                size="sm"
                color="neutral"
                type="button"
                onClick={() => setExpanded(allExpanded ? new Set() : new Set(departures.map((d) => d.code)))}
              >
                {allExpanded ? <ChevronsDownUp className="size-4" /> : <ChevronsUpDown className="size-4" />}
                {allExpanded ? "Collapse all" : "Expand all"}
              </FetchButton>
              <FetchButton variant="ghost" size="sm" color="neutral" type="button" onClick={exportCsv}>
                <Download className="size-4" />
                Export
              </FetchButton>
            </div>
          </div>

          <ul className="list-none flex flex-col divide-y divide-gray-100 rounded-2xl border border-gray-200">
            {departures.map((departure) => (
              <DepartureRow
                key={departure.code}
                departure={departure}
                destination={city ?? ""}
                expanded={expanded.has(departure.code)}
                searching={searching}
                onToggle={() => toggle(departure.code)}
              />
            ))}
          </ul>
        </div>
      )}
    </FetchModal>
  );
};
